import { Box, Alert, Typography, useTheme } from '@mui/material';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { AdminAccessGuard } from '@components/guards/AdminAccessGuard';
import { AdminOfflineGuard } from '@components/guards/AdminOfflineGuard';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useAllowedEventsData } from './hooks';
import { AllowedEventsHeader, AllowedEventsList } from './components';

export const AllowedEvents = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();

  const {
    selectedEvent,
    availableEvents,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving,
    saveError,
    actions
  } = useAllowedEventsData();

  const handleSave = async () => {
    await actions.saveChanges();
  };

  const handleSelectionChange = (event: string | null) => {
    actions.updateSelection(event);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isOnline) {
    return <AdminOfflineGuard children={null} />;
  }

  if (error) {
    const is404Error =
      error.message.includes('404') ||
      error.message.includes('Request failed with status code 404');
    const errorMessage = is404Error
      ? t('settings:allowedEvents.errors.notFound')
      : `${t('settings:allowedEvents.errors.loadFailed')}: ${error.message}`;

    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  return (
    <AdminAccessGuard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          backgroundColor: theme.palette.background.default
        }}
      >
        <AllowedEventsHeader
          selectedEvent={selectedEvent}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSaving}
          onSave={handleSave}
          onDiscard={actions.discardChanges}
        />

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: { xs: 2, sm: 3, md: 4 }
          }}
        >
          <Box
            sx={{
              maxWidth: 800,
              margin: '0 auto'
            }}
          >
            {saveError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {t('settings:allowedEvents.errors.saveFailed')}:{' '}
                {saveError.message}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {t('settings:allowedEvents.description')}
              </Typography>
            </Box>

            <AllowedEventsList
              availableEvents={availableEvents}
              selectedEvent={selectedEvent}
              isLoading={isLoading}
              onSelectionChange={handleSelectionChange}
            />
          </Box>
        </Box>
      </Box>
    </AdminAccessGuard>
  );
};
